import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { StyledArticle } from '@/components/ui/BerattelseStyles';

// Define a more specific type for individual article content
interface ArticleSection {
  type: 'paragraph' | 'heading' | 'list';
  content?: string; // For paragraph and heading
  level?: number; // For heading (e.g., 2 for h2)
  items?: string[]; // For list
}

interface ArticleData {
  id: string;
  pageTitle: string;
  hero: {
    title: string;
    category: string;
    author: string;
    date: string;
    imageUrl: string;
  };
  article: {
    introduction: string;
    sections: ArticleSection[];
  };
}

const TidigaTeckenPaDemens = () => {
  const [articleData, setArticleData] = useState<ArticleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // const { articleId } = useParams<{ articleId: string }>(); // Not strictly needed if component is specific

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        // Specific fetch for this article
        const response = await fetch('/data/text/pages/berattelser/tidiga-tecken-pa-demens.json');
        if (!response.ok) {
          throw new Error(`Failed to fetch article: ${response.statusText}`);
        }
        const data = await response.json();
        setArticleData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        console.error("Error loading article:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, []); // Fetch once on mount

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-8 pb-16 flex items-center justify-center">
          <p>Laddar artikel...</p>
        </main>
      </div>
    );
  }

  if (error || !articleData) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-8 pb-16 flex flex-col items-center justify-center text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Kunde inte ladda artikeln</h1>
          <p className="text-gray-600 mb-6">{error || "Artikeln kunde inte hittas."}</p>
          <Button asChild>
            <Link to="/berattelser">
              <ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka till Berättelser
            </Link>
          </Button>
        </main>
      </div>
    );
  }

  const { hero, article } = articleData;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-8 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="mb-8">
            <Button variant="outline" asChild className="mb-6">
              <Link to="/berattelser">
                <ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka till Berättelser
              </Link>
            </Button>
            <h1 className="text-3xl md:text-4xl font-bold text-minnestig-teal mb-3">{hero.title}</h1>
            <div className="flex items-center text-sm text-gray-500 mb-1">
              <span>{hero.author}</span>
              <span className="mx-2">|</span>
              <span>{new Date(hero.date).toLocaleDateString('sv-SE')}</span>
              <span className="mx-2">|</span>
              <span className="px-2 py-0.5 rounded-full bg-minnestig-lavender/50 text-minnestig-purple text-xs font-medium">
                {hero.category}
              </span>
            </div>
            {hero.imageUrl && (
              <img src={hero.imageUrl} alt={hero.title} className="w-full aspect-square object-cover rounded-lg my-6" />
            )}
          </div>

          <div className="space-y-6">
            <StyledArticle introduction={article.introduction} sections={article.sections} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default TidigaTeckenPaDemens;
